import React, { FunctionComponent, useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { useMutation } from '@tanstack/react-query';

import { fetch_one } from '../../../../lib/v31lib';
import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import {
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button as Btn,
  Editor,
  Typography
} from '../../../../components/v2/styled';

import { User } from '../../../../models/User';

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

type Props = {
  object: User;
  objectRefetch?: () => void;
}

// Interne notitie op de client, per praktijk (users.extra_data.internal_comments
// [<practice_id>] via deep-merge save). Geen settings: de widget heeft niets
// te configureren. Drie modi:
// - readonly (default): inhoud als html Typography, edit- + hide-knop
// - editing: advanced Editor, save + cancel in de plaats van de knoppen
// - hidden: inhoud weg, show-knop in de plaats
export const CoachUserWidgetsInternalComment: FunctionComponent<Props> = ({
  object,
  objectRefetch
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const {t, i18n} = useTranslation(['translations']);

  const comment = HelpersClientWidgets.getInternalComment(object?.extra_data, currentPractice?.id);

  const [mode, setMode] = useState<'readonly' | 'editing' | 'hidden'>('readonly');
  const [draft, setDraft] = useState<string>('');

  // Bij wisselen van client (of eerste load): terug naar readonly.
  useEffect(() => {
    setMode('readonly');
  }, [object?.id]);

  const mutationSave = useMutation({
    mutationFn: (pars: { formData: any }) =>
      fetch_one(`/${i18n.resolvedLanguage || 'nl'}/v3/objects/save`, pars.formData, login),
    onSuccess: () => {
      objectRefetch?.();
    },
  });

  const startEdit = () => {
    setDraft(comment);
    setMode('editing');
  };

  const doSave = () => {
    mutationSave.mutate({
      formData: {
        object: 'user',
        id: object?.id,
        ob: {
          id: object?.id,
          extra_data: {
            internal_comments: {
              [String(currentPractice?.id)]: draft
            }
          }
        },
        fields: ['id']
      }
    });
    setMode('readonly');
  };

  const doCancel = () => {
    setDraft('');
    setMode('readonly');
  };

  return <Paper sx={{padding: 2, marginTop: 1}}>
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.internal_comment")}</Typography>
        <Divider />
      </Grid>

      {/* Readonly: de inhoud. Leeg = italic placeholder. */}
      {mode === 'readonly' && <Grid item xs={12}>
        {!!comment
          ? <Typography variant="body2" html>{comment}</Typography>
          : <Typography variant="body2" sx={{fontStyle: 'italic', color: 'text.secondary'}} html>{t("coach.submenu.detail.internal_comment_empty")}</Typography>}
      </Grid>}

      {/* Editing: de advanced editor (zelfde als consult_reden), extra
          hoogte (360 i.p.v. de 240px default) — de widget heeft ruimte
          genoeg voor een notitie. */}
      {mode === 'editing' && <Grid item xs={12}>
        <Editor
          advanced
          height={360}
          label={t("coach.submenu.detail.internal_comment")}
          value={draft}
          onChange={setDraft}
        />
      </Grid>}

      {/* Actierij: edit + hide in readonly, save + cancel tijdens editing,
          show in hidden. Text-knoppen rechts onderaan (compact). */}
      <Grid item xs={12} sx={{display: 'flex', justifyContent: 'flex-end', gap: 0.5}}>
        {mode === 'readonly' && <>
          <Btn
            label={t("coach.general.actions.edit")}
            id="internal_comment_edit"
            text
            size="small"
            startIcon={<EditIcon />}
            onClick={startEdit}
          />
          {!!comment && <Btn
            label={t("coach.general.actions.hide")}
            id="internal_comment_hide"
            text
            size="small"
            startIcon={<VisibilityOffIcon />}
            onClick={() => setMode('hidden')}
          />}
        </>}

        {mode === 'editing' && <>
          <Btn
            label={t("coach.general.actions.cancel")}
            id="internal_comment_cancel"
            text
            size="small"
            onClick={doCancel}
          />
          <Btn
            label={t("coach.general.actions.save")}
            id="internal_comment_save"
            text
            size="small"
            disabled={mutationSave.isPending}
            onClick={doSave}
          />
        </>}

        {mode === 'hidden' && <Btn
          label={t("coach.general.actions.show")}
          id="internal_comment_show"
          text
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={() => setMode('readonly')}
        />}
      </Grid>
    </Grid>
  </Paper>;
}
